import { Link } from 'components/core-ui/Link'
import s from '../partners.modules.scss'
import { Table } from 'components/core-ui/Table'
import { useEffect, useMemo, useState } from 'react'
import { getColumns, getFilters } from './helpers'
import { makeCoreGetRequest } from 'utils/api'
import { toTableGETUrl } from 'components/core-ui/Table/serializers'
import { toTableRowItems } from './serializers'
import { useNotification } from 'hooks'
import { formatErrorMessage } from 'utils/formatting'
import { useExternalTypesQuery, useManagersQuery, useStatusAssignmentQuery } from '../api'
import { useNavigate } from 'react-router-dom'
import { URLS } from 'components/iam/constants'
import {
  AddShoppingCart,
  BusinessOutlined,
  Groups,
  GroupsOutlined,
  PersonOutlineOutlined
} from '@mui/icons-material'
import { AddActionIcon, ViewActionIcon } from 'icons'
import { NewAssignmentModal } from '../common/NewAssignmentModal'
import { CircularProgress } from '@mui/material'

const Assignments = () => {
  // TODO define interfaces for table items
  const [tableItems, setTableItems] = useState<any[]>([])
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [tableFilters, setTableFilters] = useState<any>({})
  const [totalCount, setTotalCount] = useState<number>(0)
  const [shouldRefreshData, setShouldRefreshData] = useState<boolean>(false)
  const [shouldResetPage, setShouldResetPage] = useState<boolean>(false)
  const [externalPartnerTypeOptions, setExternalPartnerTypeOptions] = useState<
    AutocompleteOption[]
  >([])
  const [statusOptions, setStatusOptions] = useState<AutocompleteOption[]>([])
  const [managersOptions, setManagersOptions] = useState<AutocompleteOption[]>([])
  const [isNewModalOpen, setIsNewModalOpen] = useState<boolean>(false)

  const { showNotification } = useNotification()

  const navigate = useNavigate()

  const openNewModal = (): void => setIsNewModalOpen(true)
  const closeNewModal = (): void => setIsNewModalOpen(false)

  const { data: externalTypesData, isLoading: externalTypesLoading } = useExternalTypesQuery(
    showNotification,
    '/iam/external-partners/external-partner-types'
  )

  const { data: statusData, isLoading: statusLoading } = useStatusAssignmentQuery(
    showNotification,
    '/iam/external-partners/statuses'
  )

  const { data: managersData } = useManagersQuery(
    showNotification,
    '/iam/external-partners/summaries/managers-slim'
  )

  useEffect(() => {
    if (externalTypesData) {
      setExternalPartnerTypeOptions(
        externalTypesData.list.map(s => ({
          label: s.name,
          value: s.id,
          extensionPeriod: s.extensionPeriod,
          group: s.group
        })) ?? []
      )
    }
  }, [externalTypesData])

  useEffect(() => {
    if (statusData) {
      setStatusOptions(
        statusData.list?.filter(s => s.isEnabled).map(s => ({ label: s.name, value: s.id })) ?? []
      )
    }
  }, [statusData])

  useEffect(() => {
    if (statusOptions.length) {
      setTableFilters({
        status: statusOptions.find(s => s.label === 'Enabled') || { label: '', value: '' }
      })
    }
  }, [statusOptions])

  useEffect(() => {
    if (managersData) {
      setManagersOptions(
        managersData.map(manager => ({
          label: manager.fullName,
          value: manager.id
        })) ?? []
      )
    }
  }, [managersData])

  const columns = useMemo(() => getColumns(), [])

  const filters = useMemo(
    () => getFilters(externalPartnerTypeOptions, statusOptions, managersOptions),
    [externalPartnerTypeOptions, statusOptions, managersOptions]
  )

  const toolbarActions = useMemo(
    () => [
      {
        label: 'Partners',
        icon: <Groups width={18} style={{ color: '#0970E6' }} />,
        onClick: () => navigate(URLS.IAM_PARTNERS_MANAGEMENT)
      },
      {
        label: 'Managers',
        icon: <PersonOutlineOutlined width={18} style={{ color: '#0970E6' }} />,
        onClick: () => navigate(URLS.IAM_MANAGERS)
      },
      {
        label: 'Delegates',
        icon: <GroupsOutlined width={18} style={{ color: '#0970E6' }} />,
        onClick: () => navigate(URLS.IAM_DELEGATES)
      },
      {
        label: 'Legal entities',
        icon: <BusinessOutlined width={18} style={{ color: '#0970E6' }} />,
        onClick: () => navigate(URLS.IAM_LEGAL_ENTITIES)
      },
      {
        label: 'Stores',
        icon: <AddShoppingCart width={18} style={{ color: '#0970E6' }} />,
        onClick: () => navigate(URLS.IAM_STORES)
      },
      {
        label: 'Add Assignment',
        icon: <AddActionIcon />,
        onClick: () => openNewModal()
      }
    ],
    []
  )

  const fetchData = async ({ page, rowsPerPage, order, orderBy, tableFilters, searchText }) => {
    setIsLoading(true)
    try {
      const response = await makeCoreGetRequest(
        toTableGETUrl({
          url: '/iam/external-partners/assignments',
          tableFilters,
          searchText,
          searchFields: ['globalId', 'jobTitle', 'person.fullName'],
          page,
          rowsPerPage,
          order,
          orderBy
        })
      )

      setTableItems(toTableRowItems(response.list || []))
      setTotalCount(response.page.totalCount)
      setIsLoading(false)
    } catch (error) {
      showNotification({
        message: `There was an error fetching assignments. ${formatErrorMessage(error)}`,
        type: 'error'
      })
    }
  }

  return (
    <div className={s.mainContainer}>
      <div className={s.breadcrumbsContainer}>
        <Link href="/iam/partners" className={s.breadcrumbsLink}>
          External Partners Management
        </Link>
        <span className={s.breadcrumbsSeparator}>|</span>
        <span className={s.breadcrumbsCurrent}>Assignments</span>
      </div>
      <NewAssignmentModal
        isModalOpen={isNewModalOpen}
        closeModal={closeNewModal}
        externalPartnerTypeOptions={externalPartnerTypeOptions.filter(
          s => !s.label.toLowerCase().endsWith('_old')
        )}
        setShouldRefreshData={setShouldRefreshData}
      />
      {!statusOptions.length ? (
        <CircularProgress color="primary" style={{ margin: '40px auto' }} disableShrink />
      ) : (
        <Table
          hideCheckbox
          hasSearchInput
          orderBy="id"
          order="desc"
          columns={columns}
          items={tableItems}
          fetchData={fetchData}
          isLoading={isLoading}
          totalCount={totalCount}
          shouldRefreshData={shouldRefreshData}
          setShouldRefreshData={setShouldRefreshData}
          shouldResetPage={shouldResetPage}
          setShouldResetPage={setShouldResetPage}
          rowActions={[
            {
              label: 'Details',
              labelColor: 'white',
              typographyVariant: 'medium',
              backgroundColor: '#0970E6',
              desktopIcon: <ViewActionIcon />,
              onClick: (_e: React.MouseEvent<HTMLElement>, rowId: number) =>
                navigate(`/iam/externalPartners/assignments/${rowId}`)
            }
          ]}
          rowActionsWidth="80px"
          desktopRowActionsWidth="50px"
          toolbarActions={toolbarActions}
          filters={filters}
        />
      )}
    </div>
  )
}

export default Assignments
